import React, { useState } from 'react';
import { SubElement } from '../../types';
import { WarningIcon } from '../../components/icons/Icons';

interface ExtraWorkCardProps {
  task: SubElement;
  isProjectManager: boolean;
  onMarkAsExtraWork: (reason: string, authorizedBy: string) => Promise<{ success: boolean; error?: string }>;
}

/**
 * Registro de trabajo pedido por el cliente fuera del alcance original
 * ("nos agrega cosas nuevas"). Separar estas tareas del trabajo base es lo
 * que permite justificar o facturar la desviación al cierre del proyecto.
 */
export const ExtraWorkCard: React.FC<ExtraWorkCardProps> = ({ task, isProjectManager, onMarkAsExtraWork }) => {
  const [formOpen, setFormOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [authorizedBy, setAuthorizedBy] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Sin marcar y sin permisos para marcarlo: la tarjeta no aporta nada.
  if (!task.isExtraWork && !isProjectManager) return null;

  const handleSubmit = async () => {
    if (!reason.trim()) {
      setError('Describe what the client requested.');
      return;
    }
    if (!authorizedBy.trim()) {
      setError('Record who authorized this work.');
      return;
    }
    setSaving(true);
    const result = await onMarkAsExtraWork(reason.trim(), authorizedBy.trim());
    setSaving(false);
    if (!result.success) {
      setError(result.error || 'Could not save. Please try again.');
      return;
    }
    setFormOpen(false);
    setReason('');
    setAuthorizedBy('');
    setError('');
  };

  if (task.isExtraWork) {
    return (
      <div className="detail-card full-width extra-work-card marked">
        <h3 className="detail-card-title">
          <WarningIcon size={16} />
          Extra work requested by client
        </h3>
        <dl className="extra-work-details">
          <dt>What was requested</dt>
          <dd>{task.extraWorkReason || '—'}</dd>
          <dt>Authorized by</dt>
          <dd>{task.extraWorkBy || '—'}</dd>
          {task.extraWorkAt && (
            <>
              <dt>Recorded</dt>
              <dd>{new Date(task.extraWorkAt).toLocaleString()}</dd>
            </>
          )}
        </dl>
      </div>
    );
  }

  return (
    <div className="detail-card full-width extra-work-card">
      <h3 className="detail-card-title">
        <WarningIcon size={16} />
        Extra work
        {!formOpen && (
          <button className="action-icon-btn add ml-auto" onClick={() => setFormOpen(true)} title="Mark as extra work">
            +
          </button>
        )}
      </h3>

      {formOpen ? (
        <div className="extra-work-form">
          <label>
            What did the client request?
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="e.g. Client asked for two extra mounting brackets not in the original drawing"
              rows={2}
              autoFocus
            />
          </label>
          <label>
            Authorized by
            <input
              type="text"
              value={authorizedBy}
              onChange={e => setAuthorizedBy(e.target.value)}
              placeholder="Name of the client contact who approved it"
            />
          </label>
          {error && <div className="status-modal-error">{error}</div>}
          <div className="extra-work-actions">
            <button className="status-modal-cancel" onClick={() => { setFormOpen(false); setError(''); }} disabled={saving}>
              Cancel
            </button>
            <button className="status-modal-confirm" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving…' : 'Mark as extra work'}
            </button>
          </div>
        </div>
      ) : (
        <p className="no-data-message">
          Mark this task as work the client added outside the original scope, so it can be tracked and justified separately.
        </p>
      )}
    </div>
  );
};
